/**
 * WorkAxis — shared page building blocks.
 */
import React, {useEffect, useRef, useState} from 'react';
import {Link} from "react-router-dom";
import {Magnetic, Reveal, ScrollGrow, Stagger, StaggerItem, WordsReveal} from "./motion";

/** Legacy CSS reveal for any remaining `.wx-reveal` elements. */
export function useReveal() {
    useEffect(() => {
        const els = document.querySelectorAll('.wx-reveal');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, {threshold: 0.12});
        els.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, []);
}

/** Section heading: kicker, title and lead reveal in sequence. */
export function SectionHead({kicker, title, lead, center = false}) {
    return (
        <Stagger className={"wx-section-head" + (center ? " is-center" : "")}>
            {kicker && <StaggerItem className="wx-kicker">{kicker}</StaggerItem>}
            {title && <StaggerItem as="h2" className="wx-h2">{title}</StaggerItem>}
            {lead && <StaggerItem as="p" className="wx-lead">{lead}</StaggerItem>}
        </Stagger>
    );
}

export function PageHero({kicker, title, lead}) {
    return (
        <header className="wx-page-hero">
            <div className="wx-hero-blobs" aria-hidden="true"><span/><span/></div>
            <div className="wx-container">
                <Reveal className="wx-kicker" y={12}>{kicker}</Reveal>
                <WordsReveal as="h1" className="wx-h1" delay={0.1}>{title}</WordsReveal>
                {lead && <Reveal as="p" className="wx-lead" delay={0.45}>{lead}</Reveal>}
            </div>
        </header>
    );
}

export function CtaSection({title, text, primaryLabel}) {
    const label = primaryLabel || "Discuss your project";
    return (
        <section className="wx-section">
            <div className="wx-container">
                <ScrollGrow className="wx-cta" from={0.9}>
                    <h2>{title || <>Have a process that's <span className="wx-grad-text">hard to scale?</span></>}</h2>
                    <p>{text || "Tell us what you're trying to automate or build. We respond within one business day."}</p>
                    <div className="wx-cta-actions">
                        <Magnetic>
                            <Link to="/contact" className="wx-btn wx-btn-primary">{label}</Link>
                        </Magnetic>
                        <a href="mailto:hello@workaxis.in" className="wx-btn wx-btn-ghost">hello@workaxis.in</a>
                    </div>
                </ScrollGrow>
            </div>
        </section>
    );
}

/** Endless, edge-faded strip of client logos (pauses on hover). */
export function LogoMarquee({logos}) {
    // Repeat so one track is always wider than the viewport.
    const row = [...logos, ...logos, ...logos];
    return (
        <Reveal className="wx-marquee is-logos">
            {[0, 1].map((dup) => (
                <div className="wx-marquee-track" aria-hidden={dup === 1} key={dup}>
                    {row.map((logo, i) => (
                        <img key={`${dup}-${i}`} src={logo.image} loading="lazy"
                             alt={dup === 0 && i < logos.length ? logo.name : ""}/>
                    ))}
                </div>
            ))}
        </Reveal>
    );
}

/** Horizontal, swipeable row of large work cards with scroll-snap and prev/next buttons. */
export function WorkCarousel({items}) {
    const ref = useRef(null);
    const [edge, setEdge] = useState({start: true, end: false});

    const update = () => {
        const el = ref.current;
        if (!el) return;
        setEdge({start: el.scrollLeft < 8, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 8});
    };

    useEffect(() => {
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, []);

    const go = (dir) => {
        const el = ref.current;
        if (!el) return;
        const card = el.querySelector('.wx-work-card');
        const step = card ? card.getBoundingClientRect().width + 20 : el.clientWidth * 0.8;
        el.scrollBy({left: dir * step, behavior: 'smooth'});
    };

    return (
        <div className="wx-work">
            <div className="wx-work-track" ref={ref} onScroll={update}>
                {items.map((item, i) => (
                    <Reveal as="a" key={item.name} href={item.link} target="_blank" rel="noreferrer"
                            className="wx-work-card" delay={i * 0.08} y={40}>
                        <div className="wx-work-media">
                            <img loading="lazy" src={item.image} alt={item.name}/>
                        </div>
                        <div className="wx-work-meta">
                            <div>
                                <h4>{item.name}</h4>
                                <p>{item.ownership}</p>
                            </div>
                            <span className="wx-work-arrow" aria-hidden="true">↗</span>
                        </div>
                    </Reveal>
                ))}
            </div>
            <div className="wx-work-nav">
                <button type="button" onClick={() => go(-1)} disabled={edge.start} aria-label="Previous work">←</button>
                <button type="button" onClick={() => go(1)} disabled={edge.end} aria-label="Next work">→</button>
            </div>
        </div>
    );
}
